/**
 * The package entry factory. `createDocumentWorkspace(options)` builds one
 * workspace instance: the canonical document-tab store (zustand), the surface
 * registry, the snapshot-adapter seam and the resolved theme/features. An
 * application creates exactly one instance at module scope — one shell per
 * document — and passes it to `<WorkspaceShell workspace={...}>`.
 *
 * Distribution model: applications alias `@suite/documents` to this source
 * tree (Vite resolve.alias + tsconfig paths) and compile it with their own
 * React/zustand — no npm install, no build step, edit-once semantics.
 */

import { create } from 'zustand';
import {
  CLOSED_STACK_LIMIT,
  DEFAULT_GROUP_ID,
  type ClosedTabEntry,
  type DocArrangement,
  type DocGroup,
  type DocLayoutNode,
  type DocSnapZone,
  type DocTab,
  type DocWindowState,
  type DocumentSnapshotAdapter,
  type DocumentTabsState,
  type DocumentWorkspace,
  type DocumentWorkspaceOptions,
  type OpenTabOptions,
  type ResizeDirection,
  type SplitDirection,
  type SurfaceDefinition,
  type WorkspaceClassNames,
  type WorkspaceFeatures,
  type WorkspaceLabels,
} from '../types';
import {
  createGroupId,
  createGroup,
  firstLeafGroup,
  leafGroupIds,
  pruneEmptyLeaves,
  splitLeafWithGroup,
  updateSplitSizes,
} from '../layout/layoutTree';

/* ------------------------------------------------------------------ */
/* Persisted session storage (debounced localStorage, panels pattern)  */
/* ------------------------------------------------------------------ */

const FLUSH_DELAY_MS = 400;

/** A Storage wrapper whose writes coalesce into a trailing debounce; reads
 * stay synchronous and pending writes flush on page hide. */
const debouncedLocalStorage = (): Storage => {
  let timers = new Map<string, ReturnType<typeof setTimeout>>();
  let pending = new Map<string, string | null>();

  const flush = () => {
    timers.forEach((timer) => clearTimeout(timer));
    timers = new Map();
    pending.forEach((value, key) => {
      try {
        if (value === null) {
          localStorage.removeItem(key);
        } else {
          localStorage.setItem(key, value);
        }
      } catch {
        /* quota or privacy mode — persistence is best effort */
      }
    });
    pending = new Map();
  };

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', flush);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') flush();
    });
  }

  return {
    get length() {
      return localStorage.length;
    },
    clear: () => localStorage.clear(),
    getItem: (key: string) => localStorage.getItem(key),
    key: (index: number) => localStorage.key(index),
    removeItem: (key: string) => {
      pending.set(key, null);
      const timer = setTimeout(() => {
        timers.delete(key);
        flush();
      }, FLUSH_DELAY_MS);
      timers.set(key, timer);
    },
    setItem: (key: string, value: string) => {
      pending.set(key, value);
      const timer = setTimeout(() => {
        timers.delete(key);
        flush();
      }, FLUSH_DELAY_MS);
      timers.set(key, timer);
    },
  };
};

/* ------------------------------------------------------------------ */
/* Group helpers                                                       */
/* ------------------------------------------------------------------ */

/** Insert a tab into a group at an index (default: end), removing it from
 * its previous group. Returns the next groups record. */
const insertIntoGroups = (
  groups: Record<string, DocGroup>,
  tabId: string,
  groupId: string,
  index?: number,
): Record<string, DocGroup> => {
  const next: Record<string, DocGroup> = { ...groups };
  // Remove from every other group
  Object.values(next).forEach((group) => {
    if (!group.tabIds.includes(tabId)) return;
    const remaining = group.tabIds.filter((id) => id !== tabId);
    next[group.id] = {
      ...group,
      tabIds: remaining,
      activeTabId:
        group.activeTabId === tabId ? (remaining[0] ?? null) : group.activeTabId,
    };
  });
  const target = next[groupId] ?? createGroup(groupId);
  const filtered = target.tabIds.filter((id) => id !== tabId);
  const insertIdx =
    typeof index === 'number' ? Math.min(Math.max(index, 0), filtered.length) : filtered.length;
  next[groupId] = {
    ...target,
    id: groupId,
    tabIds: [...filtered.slice(0, insertIdx), tabId, ...filtered.slice(insertIdx)],
    activeTabId: tabId,
  };
  return next;
};

const removeFromGroups = (
  groups: Record<string, DocGroup>,
  tabId: string,
): Record<string, DocGroup> => {
  const next: Record<string, DocGroup> = { ...groups };
  Object.values(next).forEach((group) => {
    if (!group.tabIds.includes(tabId)) return;
    const remaining = group.tabIds.filter((id) => id !== tabId);
    next[group.id] = {
      ...group,
      tabIds: remaining,
      activeTabId:
        group.activeTabId === tabId ? (remaining[0] ?? null) : group.activeTabId,
    };
  });
  return next;
};

const createStoreSlice = (namespace: string) =>
  create<DocumentTabsState>()((set, get) => ({
    tabs: {},
    tabIds: [],
    groups: { [DEFAULT_GROUP_ID]: createGroup(DEFAULT_GROUP_ID) },
    layout: { type: 'leaf', groupId: DEFAULT_GROUP_ID },
    windowStates: {},
    arrangement: 'tabs',
    activeTabId: null,
    activeGroupId: null,
    closedStack: [],

    openTab: (tab, options) => {
      const state = get();
      const groupId =
        options?.groupId ??
        (state.layout.type === 'leaf'
          ? state.layout.groupId
          : firstLeafGroup(state.layout));
      const nextGroups = insertIntoGroups(state.groups, tab.id, groupId, options?.index);
      set((prev) => ({
        tabs: { ...prev.tabs, [tab.id]: tab },
        tabIds: prev.tabIds.includes(tab.id) ? prev.tabIds : [...prev.tabIds, tab.id],
        groups: nextGroups,
        windowStates: {
          ...prev.windowStates,
          [tab.id]: { mode: 'tab', groupId },
        },
        activeTabId: options?.activate === false ? prev.activeTabId : tab.id,
      }));
    },

    closeTab: (id) => {
      const state = get();
      const tab = state.tabs[id];
      if (!tab) return;
      const groupId = state.windowStates[id]?.groupId ?? null;
      const groupOrder = groupId ? (state.groups[groupId]?.tabIds ?? []) : [];
      const indexInGroup = groupOrder.indexOf(id);
      const entry: ClosedTabEntry = {
        tab: { ...tab },
        groupId,
        index: indexInGroup >= 0 ? indexInGroup : groupOrder.length,
      };
      const tabIds = state.tabIds.filter((tabId) => tabId !== id);
      const wasActive = state.activeTabId === id;
      set((prev) => ({
        tabs: Object.fromEntries(Object.entries(prev.tabs).filter(([key]) => key !== id)),
        tabIds,
        groups: removeFromGroups(prev.groups, id),
        windowStates: Object.fromEntries(
          Object.entries(prev.windowStates).filter(([key]) => key !== id),
        ),
        activeTabId: wasActive ? (tabIds[tabIds.length - 1] ?? null) : prev.activeTabId,
        closedStack: [entry, ...prev.closedStack].slice(0, CLOSED_STACK_LIMIT),
      }));
      get().collapseEmptyGroups();
    },

    activateTab: (id) => {
      const state = get();
      if (state.activeTabId === id) return;
      if (!state.tabs[id]) return;
      set({ activeTabId: id });
    },

    updateTab: (id, patch) => {
      set((prev) => {
        const tab = prev.tabs[id];
        if (!tab) return {};
        return { tabs: { ...prev.tabs, [id]: { ...tab, ...patch } } };
      });
    },

    setDirty: (id, dirty) => {
      set((prev) => {
        const tab = prev.tabs[id];
        if (!tab || !!tab.isDirty === dirty) return {};
        return { tabs: { ...prev.tabs, [id]: { ...tab, isDirty: dirty } } };
      });
    },

    pinTab: (id, pinned) => {
      set((prev) => {
        const tab = prev.tabs[id];
        if (!tab) return {};
        return {
          tabs: { ...prev.tabs, [id]: { ...tab, isPinned: pinned ?? !tab.isPinned } },
        };
      });
    },

    setWindowState: (id, patch) => {
      set((prev) => {
        const current = prev.windowStates[id] ?? { mode: 'tab' as const };
        return {
          windowStates: { ...prev.windowStates, [id]: { ...current, ...patch } },
        };
      });
    },

    setArrangement: (arrangement) => {
      set((prev) => ({
        arrangement,
        windowStates: Object.fromEntries(
          Object.entries(prev.windowStates).map(([id, ws]) => [
            id,
            // Entering float mode keeps every document visible as a floating
            // window (geometry falls back to renderer defaults); any other
            // arrangement re-docks them all
            arrangement === 'float'
              ? { ...ws, mode: 'float' as const }
              : { ...ws, mode: 'tab' as const },
          ]),
        ),
      }));
    },

    floatTab: (id, geometry) => {
      set((prev) => ({
        groups: removeFromGroups(prev.groups, id),
        windowStates: {
          ...prev.windowStates,
          [id]: {
            mode: 'float',
            position: geometry?.position,
            size: geometry?.size,
          },
        },
      }));
      get().collapseEmptyGroups();
    },

    dockTab: (id, groupId, index) => {
      const state = get();
      const targetGroupId =
        groupId ??
        (state.layout.type === 'leaf' ? state.layout.groupId : firstLeafGroup(state.layout));
      set((prev) => {
        if (!prev.tabs[id]) return {};
        return {
          groups: insertIntoGroups(prev.groups, id, targetGroupId, index),
          windowStates: {
            ...prev.windowStates,
            [id]: { mode: 'tab', groupId: targetGroupId },
          },
          activeTabId: id,
          ...(prev.arrangement === 'float' ? { arrangement: 'tabs' as const } : null),
        };
      });
      get().collapseEmptyGroups();
    },

    splitWithTab: (id, direction, position) => {
      const state = get();
      if (!state.tabs[id]) return;
      const ws = state.windowStates[id];
      const currentGroupId = ws?.groupId;

      // The leaf that will host the split: the tab's own group when docked,
      // otherwise the first leaf of the tree (pik docks floating docs into
      // the layout's leading group)
      const targetGroupId =
        currentGroupId && state.groups[currentGroupId]
          ? currentGroupId
          : firstLeafGroup(state.layout);

      // Same-direction split at a two-leaf root: dock into the far group
      // instead of nesting (pik's rule; keeps tab strips flat)
      if (
        state.layout.type === 'split' &&
        state.layout.direction === direction &&
        state.layout.children[0].type === 'leaf' &&
        state.layout.children[1].type === 'leaf'
      ) {
        const siblingGroupId =
          position === 'before'
            ? (state.layout.children[0] as Extract<DocLayoutNode, { type: 'leaf' }>).groupId
            : (state.layout.children[1] as Extract<DocLayoutNode, { type: 'leaf' }>).groupId;
        get().dockTab(id, siblingGroupId);
        return;
      }

      // Remove the tab from its current group first (a docked tab leaves its
      // group; a floating tab was already in none)
      const newGroupId = createGroupId(namespace);
      let nextGroups = state.groups;
      if (currentGroupId) {
        nextGroups = removeFromGroups(nextGroups, id);
      }
      nextGroups = { ...nextGroups, [newGroupId]: createGroup(newGroupId) };

      const layout = splitLeafWithGroup(
        state.layout,
        targetGroupId,
        direction,
        position,
        newGroupId,
        createGroupId(namespace),
      );

      set({
        groups: nextGroups,
        layout,
        windowStates: {
          ...state.windowStates,
          [id]: { mode: 'tab', groupId: newGroupId },
        },
        activeTabId: id,
      });
    },

    setNodeSplitSizes: (node, sizes) => {
      set((prev) => ({ layout: updateSplitSizes(prev.layout, node, sizes) }));
    },

    collapseEmptyGroups: () => {
      const state = get();
      const { layout, removedGroupIds } = pruneEmptyLeaves(state.layout, state.groups);
      if (removedGroupIds.length === 0 && layout === state.layout) return;
      const groups = { ...state.groups };
      removedGroupIds.forEach((id) => {
        if (id !== DEFAULT_GROUP_ID) delete groups[id];
      });
      // Groups orphaned by re-parenting also leave the record
      const referenced = new Set(leafGroupIds(layout));
      Object.keys(groups).forEach((id) => {
        if (!referenced.has(id) && id !== DEFAULT_GROUP_ID) delete groups[id];
      });
      set({ layout, groups });
    },

    reorderInGroup: (groupId, fromIndex, toIndex) => {
      set((prev) => {
        const group = prev.groups[groupId];
        if (!group) return {};
        const ids = [...group.tabIds];
        if (
          fromIndex < 0 ||
          fromIndex >= ids.length ||
          toIndex < 0 ||
          toIndex > ids.length ||
          fromIndex === toIndex
        ) {
          return {};
        }
        const [moved] = ids.splice(fromIndex, 1);
        const targetIdx = fromIndex < toIndex ? toIndex - 1 : toIndex;
        ids.splice(targetIdx, 0, moved);
        return {
          groups: { ...prev.groups, [groupId]: { ...group, tabIds: ids } },
        };
      });
    },

    reopenClosedTab: () => {
      const state = get();
      const [entry, ...rest] = state.closedStack;
      if (!entry) return null;
      const groupId =
        entry.groupId && state.groups[entry.groupId] ? entry.groupId : undefined;
      const groupOrder = groupId ? (state.groups[groupId]?.tabIds ?? []) : [];
      const index =
        groupId && typeof entry.index === 'number'
          ? Math.min(entry.index, groupOrder.length)
          : undefined;
      get().openTab(entry.tab, { groupId, index, activate: true });
      set({ closedStack: rest });
      return entry.tab;
    },

    setActiveGroup: (groupId) => {
      set((prev) => {
        if (prev.activeGroupId === groupId) return {};
        return { activeGroupId: groupId };
      });
    },

    setGroupActiveTab: (groupId, tabId) => {
      set((prev) => {
        const group = prev.groups[groupId];
        if (!group || !group.tabIds.includes(tabId)) return {};
        return {
          groups: {
            ...prev.groups,
            [groupId]: { ...group, activeTabId: tabId },
          },
        };
      });
    },

    splitGroup: (groupId, direction, position) => {
      const state = get();
      const targetLeaf =
        state.groups[groupId] ? groupId : firstLeafGroup(state.layout);
      if (!targetLeaf) return null;

      // Same-direction split at a two-leaf root: no new pane (kor's rule —
      // the caller docks into the far group instead)
      if (
        state.layout.type === 'split' &&
        state.layout.direction === direction &&
        state.layout.children[0].type === 'leaf' &&
        state.layout.children[1].type === 'leaf'
      ) {
        return null;
      }

      const newGroupId = createGroupId(namespace);
      const layout = splitLeafWithGroup(
        state.layout,
        targetLeaf,
        direction,
        position,
        newGroupId,
        createGroupId(namespace),
      );
      set((prev) => ({
        layout,
        groups: {
          ...prev.groups,
          [newGroupId]: createGroup(newGroupId),
        },
      }));
      return newGroupId;
    },
  }));

export type DocumentTabsStore = ReturnType<typeof createStoreSlice>;

export type { DocSnapZone, ResizeDirection, SplitDirection };

/* ------------------------------------------------------------------ */
/* Defaults for classNames / features / labels                         */
/* ------------------------------------------------------------------ */

const DEFAULT_CLASS_NAMES: WorkspaceClassNames = {
  emptyState: 'doc-empty',
  groupPane: 'doc-group-pane',
  windowsArea: (arrangement: DocArrangement) =>
    `doc-windows-area doc-arrangement-${arrangement}`,
  splitContainer: (direction: SplitDirection) =>
    `doc-split-container doc-split-${direction}`,
  splitDivider: (direction: SplitDirection) =>
    `doc-split-divider doc-split-${direction}`,
  dividerDragging: 'doc-divider-dragging',
  tabStrip: 'doc-tab-strip',
  tab: 'doc-tab',
  tabActive: 'doc-tab-active',
  tabDragging: 'doc-tab-dragging',
  tabDockPreview: 'doc-tab-dock-preview',
  tabLabel: 'doc-tab-label',
  tabDirty: 'doc-tab-dirty',
  tabPin: 'doc-tab-pin',
  dropIndicator: 'doc-drop-indicator',
  emptyTabsHint: 'doc-tabs-empty-hint',
  window: 'doc-window',
  windowInactive: 'doc-window-inactive',
  windowTitleBar: 'doc-window-title-bar',
  windowTitle: 'doc-window-title',
  windowTitleControls: 'doc-window-title-controls',
  windowTitleButton: 'doc-window-title-button',
  resizeHandle: (dir: ResizeDirection) => `doc-resize-handle doc-resize-${dir}`,
  snapPreview: (zone: DocSnapZone, fullscreen: boolean) =>
    fullscreen ? 'doc-snap-preview doc-snap-fullscreen' : `doc-snap-preview doc-snap-${zone}`,
  overflowButton: 'doc-tab-overflow-button',
  overflowMenu: 'doc-tab-overflow-menu',
  overflowItem: 'doc-tab-overflow-item',
  overflowItemActive: 'doc-tab-overflow-item-active',
};

const DEFAULT_FEATURES: Required<WorkspaceFeatures> = {
  dirtyDot: false,
  pinning: false,
  middleClickClose: false,
  shortcuts: false,
  overflowMenu: false,
};

const DEFAULT_LABELS: WorkspaceLabels = {
  dockAsTab: (label) => `Dock ${label} as tab`,
  dockAsTabAria: (label) => `Dock ${label} as tab`,
  maximizeAria: (label) => `Maximize ${label}`,
  closeTabAria: (label) => `Close ${label}`,
  closeWindowAria: (label) => `Close ${label}`,
  overflowLabel: 'All tabs',
};

/* ------------------------------------------------------------------ */
/* Factory                                                             */
/* ------------------------------------------------------------------ */

export function createDocumentWorkspace(
  options: DocumentWorkspaceOptions,
): DocumentWorkspace & {
  store: DocumentTabsStore;
} {
  const classNames: WorkspaceClassNames = {
    ...DEFAULT_CLASS_NAMES,
    ...(options.classNames ?? {}),
  };
  const features: Required<WorkspaceFeatures> = {
    ...DEFAULT_FEATURES,
    ...(options.features ?? {}),
  };
  const labels: WorkspaceLabels = { ...DEFAULT_LABELS, ...(options.labels ?? {}) };

  const surfaces = new Map<string, SurfaceDefinition>();
  let snapshotAdapter: DocumentSnapshotAdapter | null = null;

  const store = createStoreSlice(options.storageNamespace);

  if (options.sessionRestore) {
    const key = `${options.storageNamespace}.doc_workspace`;
    // Keep the persisted mirror fresh from every state change — attached
    // unconditionally, because a first-run workspace (no snapshot yet) must
    // still persist; subscribing only when a snapshot exists would leave the
    // session dead forever on a fresh install.
    store.subscribe((state) => {
      try {
        localStorage.setItem(
          key,
          JSON.stringify({
            tabs: state.tabs,
            tabIds: state.tabIds,
            groups: state.groups,
            layout: state.layout,
            windowStates: state.windowStates,
            arrangement: state.arrangement,
            activeTabId: state.activeTabId,
            closedStack: state.closedStack,
          }),
        );
      } catch {
        /* best effort */
      }
    });
    // Rehydrate before first render: apply the persisted snapshot into the
    // fresh store, then let the app reload each tab's content.
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        const snapshot = JSON.parse(raw) as Partial<DocumentTabsState>;
        const restoredTabs = snapshot.tabIds
          ?.map((id) => snapshot.tabs?.[id])
          .filter(Boolean) as DocTab[] | undefined;
        store.setState({
          ...(snapshot.tabs ? { tabs: snapshot.tabs } : {}),
          ...(snapshot.tabIds ? { tabIds: snapshot.tabIds } : {}),
          ...(snapshot.groups ? { groups: snapshot.groups } : {}),
          ...(snapshot.layout ? { layout: snapshot.layout } : {}),
          ...(snapshot.windowStates ? { windowStates: snapshot.windowStates } : {}),
          ...(snapshot.arrangement ? { arrangement: snapshot.arrangement } : {}),
          ...(snapshot.activeTabId !== undefined ? { activeTabId: snapshot.activeTabId } : {}),
          ...(snapshot.closedStack ? { closedStack: snapshot.closedStack } : {}),
        });
        restoredTabs?.forEach((tab) => options.onSessionRestore?.(tab));
      }
    } catch {
      /* corrupt snapshot — start clean */
    }
  }

  const workspace: DocumentWorkspace & { store: DocumentTabsStore } = {
    namespace: options.storageNamespace,
    classNames,
    features,
    labels,
    registerSurface: (def) => {
      surfaces.set(def.kind, def);
    },
    getSurface: (kind) => surfaces.get(kind),
    setSnapshotAdapter: (adapter) => {
      snapshotAdapter = adapter;
    },
    getSnapshotAdapter: () => snapshotAdapter,
    store,
  };

  return workspace;
}

/** Debounced localStorage wrapper, exported for apps that persist their own
 * workspace-adjacent state with the same write-coalescing behavior. */
export { debouncedLocalStorage };
